import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core'
import { MaintenanceMessageService, PublicMaintenanceMessage } from '@mmx/shared'
import { catchError, of, Subject, takeUntil } from 'rxjs'

@Component({
  selector: 'mmx-maintenance-banner',
  templateUrl: './maintenance-banner.component.html',
  styleUrls: ['./maintenance-banner.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class MaintenanceBannerComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() clinicId: string | null = null

  public messages: PublicMaintenanceMessage[] = []
  public expanded = false

  private allMessages: PublicMaintenanceMessage[] = []
  // dismissals only last for the current page load, not persisted across refreshes
  private dismissedIds: string[] = []
  private onDestroy$ = new Subject<void>()
  private resizeObserver: ResizeObserver | undefined = undefined

  constructor(
    private maintenanceMessageService: MaintenanceMessageService,
    private changeDetectorRef: ChangeDetectorRef,
    private elementRef: ElementRef<HTMLElement>,
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (!changes.clinicId) {
      return
    }

    this.onDestroy$.next()
    this.allMessages = []
    this.messages = []
    this.expanded = false
    this.changeDetectorRef.markForCheck()

    const messages$ = this.clinicId
      ? this.maintenanceMessageService.getActive(this.clinicId)
      : this.maintenanceMessageService.getActiveGlobal()

    messages$
      .pipe(
        catchError(() => of([] as PublicMaintenanceMessage[])),
        takeUntil(this.onDestroy$),
      )
      .subscribe((messages) => {
        this.allMessages = messages || []
        this.applyDismissed()
        this.changeDetectorRef.markForCheck()
      })
  }

  ngAfterViewInit() {
    if (typeof ResizeObserver === 'undefined') {
      return
    }

    this.resizeObserver = new ResizeObserver(([entry]) => {
      document.documentElement.style.setProperty('--maintenance-banner-height', `${entry.contentRect.height}px`)
    })
    this.resizeObserver.observe(this.elementRef.nativeElement)
  }

  ngOnDestroy() {
    this.onDestroy$.next()
    this.onDestroy$.complete()
    this.resizeObserver?.disconnect()
    document.documentElement.style.removeProperty('--maintenance-banner-height')
  }

  toggleExpanded() {
    this.expanded = !this.expanded
  }

  dismiss(message: PublicMaintenanceMessage) {
    if (!this.dismissedIds.includes(message.id)) {
      this.dismissedIds.push(message.id)
    }
    this.applyDismissed()
    this.changeDetectorRef.markForCheck()
  }

  private applyDismissed() {
    this.messages = this.allMessages.filter((message) => !this.dismissedIds.includes(message.id))
  }
}
